// Details tab content for the managed contract detail page. Composed
// sections wrap ContractOverviewHeader, the 4-card cross-tab summary
// strip, a 3-column row (Details + Term & value + Performance grade),
// full-width Extension options, 50/50 People + Notes, full-width
// Evidence links, Row 5 activity log inline preview, and an AuditFooter.
// Field grids inside cards use 'max-content 1fr' so values auto-align
// without manual width tweaks. The Term & value card scopes term dates
// to the field grid; the dollar figure and its GST basis live solely on
// the Total contract value tile so there is one source of truth on the
// surface. The Performance card hosts the new GradeLetter + 3 PillarDial
// composite, with the org-level grade threshold offset wired in from
// the current organisation so admin tuning ripples through immediately.
// ?openExport=<exportId> deep-link: on load, polls the export record and
// either triggers a download (ready), shows a pending banner, or shows a
// failed banner. The param is stripped after first handling to prevent
// re-firing on re-renders.

import { useMemo, useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router";
import { ArrowUpRight, UserCog } from "lucide-react";
import { Card } from "../common";
import Alert from "../common/Alert";
import SlideOverDrawer from "../common/SlideOverDrawer";
import AccessPeoplePanel from "./AccessPeoplePanel";
import { ContractOverviewHeader } from "./contract-overview/ContractOverviewHeader";
import { pluraliseExtensionPeriod } from "../../lib/contract-overview/pluralise";
import { TermProgressBar } from "./contract-overview/TermProgressBar";
import { AuditFooter } from "./contract-overview/AuditFooter";
import ActivityLogSection from "./contract-overview/ActivityLogSection";
import { PersonCardRoleBound } from "./contract-overview/PersonCardRoleBound";
import { EvidenceLinksDisplay } from "./contract-overview/EvidenceLinksDisplay";
import { ContractHealthSummary } from "./contract-overview/ContractHealthSummary";
import { PerformanceGrade } from "./contract-overview/PerformanceGrade";
import {
  gradeFromPerformanceResult,
  pillarToScore,
} from "./contract-overview/performanceGradeHelpers";
import PerformanceGradeDrawer from "./contract-overview/PerformanceGradeDrawer";
import {
  DEFAULT_SUCCESS_THRESHOLD,
  DEFAULT_WATCH_THRESHOLD,
} from "../../lib/contract-overview/contractPerformance";
import { useCurrentOrganisation } from "../../hooks/useCurrentOrganisation";
import ContractValueCard from "./tracking/variations-sows/ContractValueCard";
import { totalContractValue } from "./tracking/variations-sows/contractValueDerivation";
import { useTrackingItems } from "../../hooks/useTracking";
import { useContractHealth } from "../../hooks/useContractHealth";
import { useContractPerformance } from "../../hooks/useContractPerformance";
import {
  isSow,
  isVariation,
  ownerOf,
  managerOf,
  type ManagedContract,
  type DurationUnit,
  type Sow,
  type Variation,
} from "../../types";
import { useExport } from "../../hooks/useContractExports";
import { exportApi } from "../../services/api";

interface ContractDetailsTabProps {
  contract: ManagedContract;
}

function formatDate(dateStr: string | undefined): string {
  // Stored rows can lack dates (sparse seeded/legacy rows) — render the
  // detail-surface empty placeholder rather than "Invalid Date".
  if (!dateStr) return "—";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// Plural-aware rendering of an extension duration. Routes through the
// shared pluraliseExtensionPeriod for years/months and falls through to
// a local "1 day" / "N days" form for the rare days unit. Replaces the
// raw "{value} {unit}" template that produced the brand-flagged
// "1 years per extension" string for value=1.
function formatExtensionDuration(value: number, unit: DurationUnit): string {
  if (unit === "years") return pluraliseExtensionPeriod({ years: value, months: 0 });
  if (unit === "months") return pluraliseExtensionPeriod({ years: 0, months: value });
  return `${value} ${value === 1 ? "day" : "days"}`;
}

function addDuration(date: Date, value: number, unit: DurationUnit): Date {
  const result = new Date(date);
  if (unit === "years") result.setFullYear(result.getFullYear() + value);
  else if (unit === "months") result.setMonth(result.getMonth() + value);
  else result.setDate(result.getDate() + value);
  return result;
}

function computePeriodDates(
  contractEndDate: string,
  durationValue: number,
  durationUnit: DurationUnit,
  periodNumber: number,
): { start: string; end: string } {
  const baseEnd = new Date(contractEndDate + "T00:00:00");
  let periodStart = new Date(baseEnd);
  periodStart.setDate(periodStart.getDate() + 1);

  for (let i = 1; i < periodNumber; i++) {
    periodStart = addDuration(periodStart, durationValue, durationUnit);
  }

  const periodEnd = addDuration(periodStart, durationValue, durationUnit);
  periodEnd.setDate(periodEnd.getDate() - 1);

  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  return { start: fmt(periodStart), end: fmt(periodEnd) };
}

export default function ContractDetailsTab({
  contract,
}: ContractDetailsTabProps) {
  const contractManagerPerson = managerOf(contract);
  const contractOwnerPerson = ownerOf(contract);
  // Additional people (free-text role labels) — render alongside the
  // canonical owner + manager cards in the People tile so stakeholders
  // added via the Access & People drawer aren't invisible on the Details
  // surface. Role string is rendered verbatim as the card label.
  const additionalPeople = (contract.contractPeople ?? []).filter(
    (p) => p.role !== "owner" && p.role !== "manager",
  );
  const hasExtensionOptions = !!contract.extensionOptions;
  const hasNotes = !!(contract.notes && contract.notes.trim().length > 0);
  const hasLinks = !!(contract.links && contract.links.length > 0);

  // ?openExport= deep-link: read, handle once, strip
  const [searchParams, setSearchParams] = useSearchParams();
  const openExportId = searchParams.get("openExport") ?? undefined;
  const handledRef = useRef(false);
  const [exportBanner, setExportBanner] = useState<{
    type: "pending" | "failed";
  } | null>(null);

  const { data: exportRecord } = useExport(contract.id, openExportId);

  useEffect(() => {
    if (!openExportId || !exportRecord || handledRef.current) return;
    if (
      exportRecord.status !== "ready" &&
      exportRecord.status !== "pending" &&
      exportRecord.status !== "failed"
    )
      return;

    handledRef.current = true;

    // Strip param before triggering side effects so re-renders don't re-fire
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("openExport");
        return next;
      },
      { replace: true },
    );

    if (exportRecord.status === "ready") {
      // Authenticated blob download — S3 host is never exposed. A JSON { url }
      // fallback (large exports) is handled transparently inside downloadExport.
      // Pick the format that's actually present (prefer pdf, fall back to xlsx).
      const fmt: "pdf" | "xlsx" = exportRecord.artefactKeys?.pdf
        ? "pdf"
        : "xlsx";
      exportApi
        .downloadExport(contract.id, openExportId, fmt)
        .catch(() => {
          setExportBanner({ type: "failed" });
        });
    } else {
      // This effect strips the ?openExport URL param via setSearchParams and initiates
      // an async download; setExportBanner is inseparable from those side effects and
      // fires exactly once (ref-guarded by handledRef).
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setExportBanner({ type: exportRecord.status });
    }
  }, [openExportId, exportRecord, contract.id, setSearchParams]);

  const {
    health,
    hasRecords,
    error: healthError,
  } = useContractHealth(contract.id);
  const { result: performance, error: performanceError } =
    useContractPerformance(contract.id, {
      successThreshold: contract.performanceSuccessThreshold,
      watchThreshold: contract.performanceWatchThreshold,
    });
  const { data: organisation } = useCurrentOrganisation();
  const [perfDrawerOpen, setPerfDrawerOpen] = useState(false);
  // Access & People drawer state. ?openPeopleAccess=1 is the deep-link
  // entry point for legacy ?tab=people-access bookmarks (the parent
  // page rewrites them; we just read the flag and strip it once).
  const openPeopleParam = searchParams.get("openPeopleAccess") === "1";
  const [accessDrawerOpen, setAccessDrawerOpen] = useState(openPeopleParam);
  // useState(openPeopleParam) already initialises the drawer open when the
  // param is present. The effect only needs to strip the param from the URL.
  useEffect(() => {
    if (!openPeopleParam) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("openPeopleAccess");
        return next;
      },
      { replace: true },
    );
  }, [openPeopleParam, setSearchParams]);
  const successThreshold =
    contract.performanceSuccessThreshold ?? DEFAULT_SUCCESS_THRESHOLD;
  const watchThreshold =
    contract.performanceWatchThreshold ?? DEFAULT_WATCH_THRESHOLD;
  const gradeOffset = organisation?.performanceGradeThresholdOffset ?? 0;
  const performanceGrade = useMemo(
    () => gradeFromPerformanceResult(performance, watchThreshold, gradeOffset),
    [performance, watchThreshold, gradeOffset],
  );

  const sowQuery = useTrackingItems(contract.id, "sow", {
    limit: 200,
    includeArchived: false,
  });
  const variationQuery = useTrackingItems(contract.id, "variation", {
    limit: 200,
    includeArchived: false,
  });
  const sows: Sow[] = useMemo(
    () => (sowQuery.data?.items ?? []).filter(isSow),
    [sowQuery.data],
  );
  const variations: Variation[] = useMemo(
    () => (variationQuery.data?.items ?? []).filter(isVariation),
    [variationQuery.data],
  );
  const valueBreakdown = useMemo(
    () => totalContractValue({ value: contract.value }, sows, variations),
    [contract.value, sows, variations],
  );

  return (
    <div className="space-y-6">
      {exportBanner && (
        <div
          role="status"
          className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-body-sm text-amber-800 dark:border-amber-600 dark:bg-amber-950 dark:text-amber-200"
        >
          {exportBanner.type === "pending"
            ? "Your export is still generating — you'll receive an email when it's ready."
            : "Export failed — please try again from the Export button."}
        </div>
      )}

      <ContractOverviewHeader contractId={contract.id} />

      {healthError ? (
        <Alert variant="error">
          Contract health is unavailable — tracking data failed to load.
        </Alert>
      ) : (
        <ContractHealthSummary
          contractId={contract.id}
          health={health}
          hasRecords={hasRecords}
        />
      )}

      {/* Row 1: two-column — Details + Term & value (equal split) */}
      <div
        className="grid grid-cols-1 lg:grid-cols-2 gap-6"
        data-testid="overview-row-1"
      >
        <Card padding="md">
          <h2 className="text-h4 text-slate-900 dark:text-slate-50 mb-4">
            Details
          </h2>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-3">
            <DetailRow label="Type">
              {contract.type.charAt(0).toUpperCase() + contract.type.slice(1)}
            </DetailRow>
            <DetailRow label="Supplier">{contract.supplierName}</DetailRow>
            <DetailRow label="Contract Type">
              {contract.contractTypeLabel}
            </DetailRow>
            <DetailRow label="Business Unit">
              {contract.businessUnitLabel}
            </DetailRow>
            {contract.procurementMethodLabel && (
              <DetailRow label="Procurement Method">
                {contract.procurementMethodLabel}
              </DetailRow>
            )}
            <DetailRow label="Reference">
              <span className="font-mono">{contract.contractNumber}</span>
            </DetailRow>
          </dl>
        </Card>

        <Card padding="md">
          <h2 className="text-h4 text-slate-900 dark:text-slate-50 mb-4">
            Term &amp; value
          </h2>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-3 mb-4">
            <DetailRow label="Start date">
              {formatDate(contract.startDate)}
            </DetailRow>
            <DetailRow label="End date">
              {formatDate(contract.endDate)}
            </DetailRow>
          </dl>
          <TermProgressBar contract={contract} showDateCaption={false} />
          <div className="mt-4">
            <ContractValueCard
              contractValue={valueBreakdown.breakdown.head}
              sowsValue={valueBreakdown.breakdown.sows}
              variationsValue={valueBreakdown.breakdown.variations}
              gstApplies={contract.gstApplies}
            />
          </div>
        </Card>
      </div>

      {/* Row 2: full-width Performance card — letter + dials lay out
          horizontally given the generous row width */}
      <Card padding="md" data-testid="overview-row-performance">
        <div className="mb-4 flex items-baseline justify-between gap-3">
          <h2 className="text-h4 text-slate-900 dark:text-slate-50">
            Performance
          </h2>
          <button
            type="button"
            onClick={() => setPerfDrawerOpen(true)}
            data-testid="performance-grade-how-calculated"
            className="inline-flex items-center gap-1 text-caption text-brand-600 hover:text-brand-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-brand-400 dark:hover:text-brand-300"
          >
            History &amp; calculation
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
        {performanceError ? (
          <Alert variant="error">
            Performance is unavailable — tracking data failed to load.
          </Alert>
        ) : (
          <PerformanceGrade
            result={performance}
            successThreshold={contract.performanceSuccessThreshold}
            watchThreshold={contract.performanceWatchThreshold}
            gradeThresholdOffset={organisation?.performanceGradeThresholdOffset}
          />
        )}
        <PerformanceGradeDrawer
          isOpen={perfDrawerOpen}
          onClose={() => setPerfDrawerOpen(false)}
          result={performanceGrade}
          pillars={[
            {
              label: "Deliverables",
              score: pillarToScore(performance.deliverables),
              sampleSize: performance.deliverables.sampleSize,
            },
            {
              label: "Milestones",
              score: pillarToScore(performance.milestones),
              sampleSize: performance.milestones.sampleSize,
            },
            {
              label: "KPIs",
              score: pillarToScore(performance.kpis),
              sampleSize: performance.kpis.sampleSize,
            },
          ]}
          watchThreshold={watchThreshold}
          successThreshold={successThreshold}
          contractId={contract.id}
          gradeThresholdOffset={
            organisation?.performanceGradeThresholdOffset ?? null
          }
        />
      </Card>

      {/* Row 2: full-width Extension options */}
      {hasExtensionOptions && (
        <Card padding="md">
          <h2 className="text-h4 text-slate-900 dark:text-slate-50 mb-4">
            Extension options
          </h2>
          <p className="text-body-sm text-slate-600 dark:text-slate-400 mb-3">
            {formatExtensionDuration(
              contract.extensionOptions!.durationValue,
              contract.extensionOptions!.durationUnit,
            )}{" "}
            per extension
            {contract.extensionOptions!.noticePeriodDays
              ? ` (${contract.extensionOptions!.noticePeriodDays} ${
                  contract.extensionOptions!.noticePeriodDays === 1
                    ? "day"
                    : "days"
                } notice)`
              : ""}
          </p>
          <div className="space-y-2">
            {contract.extensionOptions!.periods.map((period) => {
              // Period dates anchor off the contract end date — a sparse row
              // without one still shows the period status, just no range.
              const dates = contract.endDate
                ? computePeriodDates(
                    contract.endDate,
                    contract.extensionOptions!.durationValue,
                    contract.extensionOptions!.durationUnit,
                    period.periodNumber,
                  )
                : null;
              return (
                <div
                  key={period.periodNumber}
                  className="flex items-center gap-2 text-body-sm"
                >
                  <span className="text-slate-500 dark:text-slate-400">
                    Period {period.periodNumber}:
                  </span>
                  <span
                    className={`badge-${period.exercised ? "active" : "draft"}`}
                  >
                    {period.exercised ? "Exercised" : "Available"}
                  </span>
                  {dates && (
                    <span className="text-slate-500 dark:text-slate-400">
                      {formatDate(dates.start)} — {formatDate(dates.end)}
                    </span>
                  )}
                  {period.exercisedDate && (
                    <span className="text-slate-500 dark:text-slate-400">
                      (exercised {formatDate(period.exercisedDate)})
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Row 3: 50/50 People + Notes. The People tile always renders
          because it hosts the entry point for the Access & People drawer
          even when no people are recorded yet. */}
      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
        data-testid="overview-row-3"
      >
          <Card padding="md">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-h4 text-slate-900 dark:text-slate-50">
                People
              </h2>
              <button
                type="button"
                onClick={() => setAccessDrawerOpen(true)}
                aria-label="Edit access & people"
                title="Edit access & people"
                className="inline-flex items-center gap-1 rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-brand-300"
              >
                <UserCog className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <div className="flex flex-col gap-3">
              <PersonCardRoleBound
                role="Contract Manager"
                person={
                  contractManagerPerson
                    ? {
                        name: contractManagerPerson.name,
                        email: contractManagerPerson.email,
                      }
                    : null
                }
              />
              <PersonCardRoleBound
                role="Contract Owner"
                person={
                  contractOwnerPerson
                    ? {
                        name: contractOwnerPerson.name,
                        email: contractOwnerPerson.email,
                      }
                    : null
                }
              />
              {additionalPeople.map((person, idx) => (
                <PersonCardRoleBound
                  key={`additional-${idx}`}
                  role={person.role}
                  person={{ name: person.name, email: person.email }}
                />
              ))}
            </div>
          </Card>

          <Card padding="md">
            <h2 className="text-h4 text-slate-900 dark:text-slate-50 mb-4">
              Notes
            </h2>
            {hasNotes ? (
              <p className="text-body text-slate-600 dark:text-slate-400 whitespace-pre-wrap">
                {contract.notes}
              </p>
            ) : (
              <p className="text-caption italic text-slate-500 dark:text-slate-400">
                No notes recorded
              </p>
            )}
          </Card>
        </div>

      {/* Row 4: full-width Evidence links */}
      <Card padding="md">
        <h2 className="text-h4 text-slate-900 dark:text-slate-50 mb-4">
          Evidence links
        </h2>
        <EvidenceLinksDisplay links={hasLinks ? contract.links! : []} />
      </Card>

      {/* Row 5: full-width Activity log inline preview */}
      <ActivityLogSection contractId={contract.id} />

      <AuditFooter
        createdAt={contract.createdAt}
        createdBy={contract.createdBy}
        updatedAt={contract.updatedAt}
        updatedBy={contract.updatedBy}
      />

      <SlideOverDrawer
        isOpen={accessDrawerOpen}
        onClose={() => setAccessDrawerOpen(false)}
        title="Access & People"
        subtitle={contract.title}
        width="lg"
      >
        <AccessPeoplePanel contract={contract} contractId={contract.id} />
      </SlideOverDrawer>
    </div>
  );
}

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <dt className="text-body-sm text-slate-500 dark:text-slate-400">
        {label}
      </dt>
      <dd className="text-body-sm text-slate-900 dark:text-slate-50">
        {children}
      </dd>
    </>
  );
}
